import React, { memo, useCallback } from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { EditEmailModal } from './edit-email-modal';
import { Heading, Text, TextProps, Button } from '@restworld/ui-ds-base';
import NextLink from 'next/link';
import { EditPhoneNumberModal } from './phone-number/edit-phone-number-modal';
import { FlexCol } from './header';
import { useAuth } from '@restworld/feature-authentication';
import { DeleteAccountModal } from './delete-account-modal';
import { useSetAtom } from 'jotai';
import { modalOpen as openPhoneNumberModal, setIsEnterPhoneNumber } from './phone-number/states';
import { useTranslation } from 'next-i18next';

import { cn } from '@restworld/utils-common';
export const AccountSettingsTab = memo(() => {
  const onOpenPhoneNumberModal = useSetAtom(openPhoneNumberModal);
  const setEnterPhoneNumberContent = useSetAtom(setIsEnterPhoneNumber);
  const { t } = useTranslation('user-settings');
  const { user } = useAuth();
  const email = user?.email;
  const phoneNumber = user?.phone_number;
  const isPhoneNumberVerified = user?.phone_number_verified;
  const isLeadUserType = user?.type === 'lead';

  const handleOpen = useCallback(() => {
    setEnterPhoneNumberContent(true);
    onOpenPhoneNumberModal(true);
  }, [onOpenPhoneNumberModal, setEnterPhoneNumberContent]);

  return (
    <FlexCol className="gap-1">
      <SectionTitle>{t('account_tab_section_title')}</SectionTitle>
      <LgColSmRow className="lg:gap-16 gap-6">
        <FlexCol className="gap-4 lg:w-[29.813rem] w-full">
          <BodyText>
            {isLeadUserType ? (
              t('account_tab_section_description_text_lead_user')
            ) : (
              <>
                {t('account_tab_section_description_text_1')}{' '}
                <span className="font-bold">{t('account_tab_section_description_text_2')}</span>{' '}
                {t('account_tab_section_description_text_3')}{' '}
                <span className="font-bold">{t('account_tab_section_description_text_4')}</span>{' '}
                {t('account_tab_section_description_text_5')}
              </>
            )}
          </BodyText>
          <NextLink
            href={isLeadUserType ? '/candidati' : '/profilo'}
            className="text-primary-800 cursor-pointer w-full"
          >
            <FlexRow className="items-center gap-1.5">
              <Text size={2} weight="medium" color="primary">
                {isLeadUserType ? t('account_tab_section_update_profile') : t('account_tab_section_edit_cv')}
              </Text>
              <ArrowRightIcon className="text-primary-800 font-medium text-sm w-4 h-4" />
            </FlexRow>
          </NextLink>
        </FlexCol>
        <FlexCol className="flex-1 items-start pt-1 gap-6 ">
          <LgColSmRow className="justify-between w-full">
            <FieldsContainer className="flex flex-col">
              <BodyText weight="medium" color="black">
                {t('email_update_enter_email_step_email_field')}
              </BodyText>
              <BodyText>{email}</BodyText>
            </FieldsContainer>
            <EditEmailModal {...{ email: email }} />
          </LgColSmRow>
          <LgColSmRow className="lg:justify-between justify-start lg:items-center items-start w-full">
            <FieldsContainer>
              <BodyText weight="medium" color="black">
                {t('account_tab_phone_number_field')}
              </BodyText>
              {phoneNumber && <BodyText>{phoneNumber}</BodyText>}
              {!phoneNumber && (
                <BodyText color="error">
                  <span className="underline">{t('account_tab_fill_phone_number_prompt')}</span>
                </BodyText>
              )}
              {phoneNumber && !isPhoneNumberVerified && (
                <Button variant="tertiary" className={'p-0 focus:ring-0 !shadow-none self-start'} onClick={handleOpen}>
                  <span className="underline">{t('account_tab_edit_phone_number_button_text')}</span>
                </Button>
              )}
            </FieldsContainer>
            <EditPhoneNumberModal />
          </LgColSmRow>
        </FlexCol>
      </LgColSmRow>
      <hr className="bg-gray-200  h-px border-0 w-full lg:mt-18.25 lg:mb-6 my-6" />
      <FlexCol>
        <SectionTitle>{t('account_tab_delete_account_prompt_text')}</SectionTitle>
        <div className="lg:w-[38.125rem] w-full mb-4 mt-1">
          <BodyText>{t('account_tab_delete_account_prompt_description')}</BodyText>
        </div>
        <DeleteAccountModal />
      </FlexCol>
    </FlexCol>
  );
});

export const LgColSmRow = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex lg:flex-row flex-col', className)} {...props} />
);

export const FlexRow = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-row', className)} {...props} />
);

export const FieldsContainer = ({ className, ...props }: any) => (
  <FlexCol className={cn('gap-1 w-full lg:mb-0 mb-6', className)} {...props} />
);

export const SectionTitle = ({ children }: { children: React.ReactNode | string }) => {
  return <Heading size={5}>{children}</Heading>;
};

export const BodyText = ({ children, ...rest }: Pick<TextProps, 'children' | 'color' | 'weight'>) => {
  return (
    <>
      {' '}
      <div className="w-full lg:block hidden whitespace-nowrap">
        <Text {...rest}>{children}</Text>
      </div>
      <div className="w-full lg:hidden block whitespace-nowrap">
        <Text size={2} {...rest}>
          {children}
        </Text>
      </div>
    </>
  );
};
